/**
 * Appearance vocabulary. Mirrors backend `app/appearance/schemas.py`: closed, typed building
 * blocks (enums, `#rrggbb` colors, typed gradients, pattern ids). There is no raw CSS anywhere:
 * `css.ts` is the only place that turns these into CSS values.
 */
export type PatternId = 'dots' | 'grid' | 'lines' | 'waves' | 'noise';
export const PATTERN_IDS: readonly PatternId[] = ['dots', 'grid', 'lines', 'waves', 'noise'];

export type LookStyle =
  'standard' | 'header' | 'border' | 'accent' | 'soft' | 'solid' | 'gradient' | 'pattern';
export const LOOK_STYLES: readonly LookStyle[] = [
  'standard',
  'header',
  'border',
  'accent',
  'soft',
  'solid',
  'gradient',
  'pattern',
];

export interface Gradient {
  start: string;
  end: string;
  /** Degrees, 0..360. */
  angle: number;
}

export interface Pattern {
  id: PatternId;
  color: string;
  background: string | null;
  /** 0.05..0.6 */
  opacity: number;
  /** 1..3 */
  scale: number;
}

export interface AdvancedColors {
  header: string | null;
  border: string | null;
  body: string | null;
}

/** How a card / the default card of a column / a board tile looks. */
export interface Look {
  v: 1;
  style: LookStyle;
  one_color: boolean;
  color: string | null;
  advanced: AdvancedColors | null;
  gradient: Gradient | null;
  pattern: Pattern | null;
  /** Explicit text colors / content surface (`#rrggbb`); absent = derived from the background. */
  title_color?: string | null;
  text_color?: string | null;
  muted_color?: string | null;
  surface?: string | null;
}

export type BackgroundKind = 'none' | 'color' | 'gradient' | 'pattern' | 'image';
export const BACKGROUND_KINDS: readonly BackgroundKind[] = [
  'none',
  'color',
  'gradient',
  'pattern',
  'image',
];

export interface Background {
  v: 1;
  kind: BackgroundKind;
  color: string | null;
  gradient: Gradient | null;
  pattern: Pattern | null;
  /** Focal point of the image, 0..1. */
  position_x: number;
  position_y: number;
  /** Darkens (>0) the image so that cards stay readable, 0..0.8. */
  overlay: number;
}

/** Everything of a column look beyond its `color` + `appearance_mode`. */
export interface ColumnLookExtra {
  v: 1;
  one_color: boolean;
  advanced: AdvancedColors | null;
  gradient: Gradient | null;
  pattern: Pattern | null;
  background: Background | null;
  /** Explicit text colors and the backing surface of the description (absent = derived). */
  title_color?: string | null;
  text_color?: string | null;
  description_color?: string | null;
  description_surface?: string | null;
  /** The inline "add a card" form: background, text and border (absent = white / dark). */
  form_background?: string | null;
  form_text?: string | null;
  form_border?: string | null;
}

export interface BoardAppearance {
  v: 1;
  tile: Look | null;
  background: Background | null;
}

export type Cover =
  | { kind: 'color'; color: string }
  | { kind: 'gradient'; start: string; end: string; angle: number }
  | { kind: 'image'; attachment_id: string; position_x: number; position_y: number };

/** The shape sent to the API for a cover (the same union). */
export type CoverInput = Cover;
